import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { getClient, getProjects, getTimeByProject, updateClient, getClientMonthlyFinancials } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { formatTime, formatCurrency, getMonthName } from '../lib/utils';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { ArrowLeft, FolderKanban, Plus, Edit, Users, UserCheck, UserX, TrendingUp, Mail, Phone, User, Table } from 'lucide-react';
import { toast } from 'sonner';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend,
} from 'recharts';

const COLORS = ['#006CB6', '#F4E600', '#000000', '#808080', '#E5E5E5'];

const clientStatuses = {
  potential: { label: 'Potenciální klient', color: 'bg-yellow-100 text-yellow-800 border-yellow-300', icon: Users },
  active: { label: 'Aktivní klient', color: 'bg-green-100 text-green-800 border-green-300', icon: UserCheck },
  churned: { label: 'Bývalý klient', color: 'bg-gray-100 text-gray-800 border-gray-300', icon: UserX },
};

const ClientDetail = () => {
  const { id } = useParams();
  const { user } = useAuth();
  const isAdmin = user?.is_admin;
  const [client, setClient] = useState(null);
  const [projects, setProjects] = useState([]);
  const [projectTime, setProjectTime] = useState([]);
  const [monthlyFinancials, setMonthlyFinancials] = useState(null);
  const [loading, setLoading] = useState(true);
  const [statusDialogOpen, setStatusDialogOpen] = useState(false);
  const [newStatus, setNewStatus] = useState('');

  const fetchData = async () => {
    try {
      const [clientRes, projectsRes] = await Promise.all([
        getClient(id),
        getProjects({ client_id: id }),
      ]);
      setClient(clientRes.data);
      setProjects(projectsRes.data);
      setNewStatus(clientRes.data.status || 'potential');

      // Financial data only for admins
      if (isAdmin) {
        try {
          const financialsRes = await getClientMonthlyFinancials(id);
          setMonthlyFinancials(financialsRes.data);
        } catch (err) {
          console.error('Error loading financials:', err);
        }
      }

      // Time tracking data
      try {
        const timeRes = await getTimeByProject(id);
        setProjectTime(timeRes.data);
      } catch (err) {
        console.error('Error loading time data:', err);
      }
    } catch (error) {
      toast.error('Chyba při načítání dat');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [id, isAdmin]);

  const handleStatusChange = async () => {
    try {
      await updateClient(id, { status: newStatus });
      toast.success('Status aktualizován');
      setStatusDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error('Chyba při změně statusu');
    }
  };

  const totalTime = projectTime.reduce((sum, p) => sum + p.total_minutes, 0);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="font-oswald text-xl uppercase tracking-wide">Načítání...</div>
      </div>
    );
  }

  if (!client) {
    return (
      <div className="text-center py-12">
        <div className="font-oswald text-xl uppercase">Klient nenalezen</div>
      </div>
    );
  }

  const statusInfo = clientStatuses[client.status] || clientStatuses.potential;
  const StatusIcon = statusInfo.icon;

  return (
    <div className="space-y-6" data-testid="client-detail">
      {/* Header */}
      <div>
        <Link
          to="/clients"
          className="inline-flex items-center gap-2 font-oswald uppercase text-sm tracking-wide text-gray-600 hover:text-black mb-4"
        >
          <ArrowLeft size={16} />
          Zpět na klienty
        </Link>
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <h1 className="font-oswald text-4xl font-bold uppercase tracking-tight">
                {client.name}
              </h1>
              <Dialog open={statusDialogOpen} onOpenChange={setStatusDialogOpen}>
                <DialogTrigger asChild>
                  <button className={`px-3 py-1 text-sm font-oswald uppercase border-2 ${statusInfo.color} hover:opacity-80 transition-opacity`} data-testid="change-status-btn">
                    <StatusIcon size={14} className="inline mr-1" />
                    {statusInfo.label}
                  </button>
                </DialogTrigger>
                <DialogContent className="border-2 border-black rounded-none shadow-[4px_4px_0px_0px_rgba(0,0,0,1)]">
                  <DialogHeader>
                    <DialogTitle className="font-oswald text-xl uppercase tracking-tight">
                      Změnit status klienta
                    </DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4">
                    <Select value={newStatus} onValueChange={setNewStatus}>
                      <SelectTrigger className="border-2 border-gray-200 rounded-none">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="potential">Potenciální klient</SelectItem>
                        <SelectItem value="active">Aktivní klient</SelectItem>
                        <SelectItem value="churned">Bývalý klient</SelectItem>
                      </SelectContent>
                    </Select>
                    <Button
                      onClick={handleStatusChange}
                      className="w-full bg-[#F4E600] text-black font-oswald uppercase tracking-wide border-2 border-black rounded-none shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] hover:translate-x-[2px] hover:translate-y-[2px] hover:shadow-[2px_2px_0px_0px_rgba(0,0,0,1)] transition-all"
                    >
                      Uložit
                    </Button>
                  </div>
                </DialogContent>
              </Dialog>
            </div>
            <p className="font-serif text-gray-600">{client.industry}</p>
          </div>
        </div>
      </div>

      {/* Client Info */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {client.contact_person && (
          <div className="flex items-center gap-3 p-4 bg-white border-2 border-gray-200">
            <User size={20} className="text-gray-400" />
            <div>
              <div className="font-oswald text-xs uppercase text-gray-500">Kontaktní osoba</div>
              <div className="font-serif">{client.contact_person}</div>
            </div>
          </div>
        )}
        {client.contact_email && (
          <div className="flex items-center gap-3 p-4 bg-white border-2 border-gray-200">
            <Mail size={20} className="text-gray-400" />
            <div>
              <div className="font-oswald text-xs uppercase text-gray-500">Email</div>
              <a href={`mailto:${client.contact_email}`} className="font-serif text-[#006CB6] hover:underline">{client.contact_email}</a>
            </div>
          </div>
        )}
        {client.contact_phone && (
          <div className="flex items-center gap-3 p-4 bg-white border-2 border-gray-200">
            <Phone size={20} className="text-gray-400" />
            <div>
              <div className="font-oswald text-xs uppercase text-gray-500">Telefon</div>
              <a href={`tel:${client.contact_phone}`} className="font-serif text-[#006CB6] hover:underline">{client.contact_phone}</a>
            </div>
          </div>
        )}
        {isAdmin && client.potential_revenue && (
          <div className="flex items-center gap-3 p-4 bg-white border-2 border-gray-200">
            <TrendingUp size={20} className="text-gray-400" />
            <div>
              <div className="font-oswald text-xs uppercase text-gray-500">Potenciální tržby/rok</div>
              <div className="font-serif font-medium">{formatCurrency(client.potential_revenue)}</div>
            </div>
          </div>
        )}
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="bg-white border-2 border-black p-6 shadow-[4px_4px_0px_0px_rgba(0,0,0,1)]">
          <div className="font-oswald text-xs uppercase tracking-wide text-gray-500 mb-1">
            Projekty
          </div>
          <div className="font-oswald text-3xl font-bold">{projects.length}</div>
        </div>
        <div className="bg-white border-2 border-black p-6 shadow-[4px_4px_0px_0px_rgba(0,0,0,1)]">
          <div className="font-oswald text-xs uppercase tracking-wide text-gray-500 mb-1">
            Celkový čas
          </div>
          <div className="font-oswald text-3xl font-bold">{formatTime(totalTime)}</div>
        </div>
        {isAdmin && monthlyFinancials && (
          <>
            <div className="bg-white border-2 border-black p-6 shadow-[4px_4px_0px_0px_rgba(0,0,0,1)]">
              <div className="font-oswald text-xs uppercase tracking-wide text-gray-500 mb-1">
                Tržby z projektů
              </div>
              <div className="font-oswald text-2xl font-bold">
                {formatCurrency(monthlyFinancials.totals.actual_revenue)}
              </div>
              <div className="text-xs text-gray-500 font-serif mt-1">
                Odhad: {formatCurrency(monthlyFinancials.totals.estimated_revenue)}
              </div>
            </div>
            <div className={`bg-white border-2 border-black p-6 shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] ${monthlyFinancials.totals.profit >= 0 ? 'bg-green-50' : 'bg-red-50'}`}>
              <div className="font-oswald text-xs uppercase tracking-wide text-gray-500 mb-1">
                Zisk
              </div>
              <div className={`font-oswald text-2xl font-bold ${monthlyFinancials.totals.profit >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                {monthlyFinancials.totals.profit >= 0 ? '+' : ''}{formatCurrency(monthlyFinancials.totals.profit)}
              </div>
              <div className="text-xs text-gray-500 font-serif mt-1">
                Náklady: {formatCurrency(monthlyFinancials.totals.costs)}
              </div>
            </div>
          </>
        )}
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Time by Project */}
        {projectTime.length > 0 && (
          <div className="bg-white border-2 border-black p-6 shadow-[4px_4px_0px_0px_rgba(0,0,0,1)]">
            <h2 className="font-oswald text-xl font-bold uppercase tracking-tight mb-6">
              Čas podle projektů
            </h2>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={projectTime}
                    cx="50%"
                    cy="50%"
                    outerRadius={80}
                    dataKey="total_minutes"
                    nameKey="project_name"
                    label={({ project_name, total_minutes }) => `${project_name}: ${formatTime(total_minutes)}`}
                    labelLine={false}
                  >
                    {projectTime.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(value) => formatTime(value)} />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}

        {/* Revenue Chart - Admin Only */}
        {isAdmin && monthlyFinancials && monthlyFinancials.monthly_data.length > 0 && (
          <div className="bg-white border-2 border-black p-6 shadow-[4px_4px_0px_0px_rgba(0,0,0,1)]">
            <h2 className="font-oswald text-xl font-bold uppercase tracking-tight mb-6">
              Finanční přehled (posl. 12 měsíců)
            </h2>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={monthlyFinancials.monthly_data.slice(-12).map(m => ({
                  name: m.month,
                  'Odhad': m.estimated_revenue,
                  'Realita': m.actual_revenue,
                  'Náklady': m.costs,
                }))}>
                  <XAxis dataKey="name" tick={{ fontFamily: 'Oswald', fontSize: 10 }} />
                  <YAxis tickFormatter={(v) => `${v / 1000}k`} />
                  <Tooltip formatter={(value) => formatCurrency(value)} />
                  <Legend />
                  <Bar dataKey="Odhad" fill="#006CB6" />
                  <Bar dataKey="Realita" fill="#F4E600" />
                  <Bar dataKey="Náklady" fill="#808080" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}
      </div>

      {/* Projects */}
      <div className="bg-white border-2 border-black p-6 shadow-[4px_4px_0px_0px_rgba(0,0,0,1)]">
        <div className="flex items-center justify-between mb-6">
          <h2 className="font-oswald text-xl font-bold uppercase tracking-tight">
            Projekty
          </h2>
          <Link
            to={`/projects?client=${id}`}
            className="inline-flex items-center gap-2 px-4 py-2 bg-black text-white font-oswald uppercase text-sm tracking-wide border-2 border-black hover:bg-gray-900 transition-all"
            data-testid="add-project-btn"
          >
            <Plus size={16} />
            Nový projekt
          </Link>
        </div>
        {projects.length === 0 ? (
          <p className="text-center font-serif text-gray-500 py-8">Zatím žádné projekty</p>
        ) : (
          <div className="space-y-3">
            {projects.map((project) => (
              <Link
                key={project.id}
                to={`/projects/${project.id}`}
                className="flex items-center justify-between p-4 border-2 border-gray-200 hover:border-black transition-colors"
                data-testid={`project-${project.id}`}
              >
                <div className="flex items-center gap-3">
                  <FolderKanban size={20} className="text-[#006CB6]" />
                  <div>
                    <div className="font-serif font-medium">{project.name}</div>
                    <div className="text-sm text-gray-500">{project.status}</div>
                  </div>
                </div>
                <div className="font-oswald text-sm">{formatTime(project.total_time_minutes)}</div>
              </Link>
            ))}
          </div>
        )}
      </div>

      {/* Monthly Financial Table - Admin Only */}
      {isAdmin && monthlyFinancials && monthlyFinancials.monthly_data.length > 0 && (
        <div className="bg-white border-2 border-black p-6 shadow-[4px_4px_0px_0px_rgba(0,0,0,1)]">
          <div className="flex items-center justify-between mb-6">
            <h2 className="font-oswald text-xl font-bold uppercase tracking-tight flex items-center gap-2">
              <Table size={20} />
              Měsíční finanční přehled
            </h2>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b-2 border-black">
                  <th className="text-left font-oswald uppercase text-xs tracking-wide py-3">Měsíc</th>
                  <th className="text-right font-oswald uppercase text-xs tracking-wide py-3">Odhad</th>
                  <th className="text-right font-oswald uppercase text-xs tracking-wide py-3">Realita</th>
                  <th className="text-right font-oswald uppercase text-xs tracking-wide py-3">Náklady</th>
                  <th className="text-right font-oswald uppercase text-xs tracking-wide py-3">Zisk</th>
                  <th className="text-right font-oswald uppercase text-xs tracking-wide py-3">Hodiny</th>
                </tr>
              </thead>
              <tbody>
                {monthlyFinancials.monthly_data.slice().reverse().map((month) => (
                  <tr key={month.month} className="border-b border-gray-200 hover:bg-gray-50">
                    <td className="py-3 font-serif font-medium">{month.month}</td>
                    <td className="py-3 text-right font-serif">{formatCurrency(month.estimated_revenue)}</td>
                    <td className="py-3 text-right font-serif">{formatCurrency(month.actual_revenue)}</td>
                    <td className="py-3 text-right font-serif text-gray-600">{formatCurrency(month.costs)}</td>
                    <td className={`py-3 text-right font-serif font-medium ${month.profit >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {month.profit >= 0 ? '+' : ''}{formatCurrency(month.profit)}
                    </td>
                    <td className="py-3 text-right font-serif text-gray-600">{month.hours.toFixed(1)}h</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="border-t-2 border-black bg-gray-50">
                  <td className="py-3 font-oswald uppercase font-bold">Celkem</td>
                  <td className="py-3 text-right font-oswald font-bold">{formatCurrency(monthlyFinancials.totals.estimated_revenue)}</td>
                  <td className="py-3 text-right font-oswald font-bold">{formatCurrency(monthlyFinancials.totals.actual_revenue)}</td>
                  <td className="py-3 text-right font-oswald font-bold text-gray-600">{formatCurrency(monthlyFinancials.totals.costs)}</td>
                  <td className={`py-3 text-right font-oswald font-bold ${monthlyFinancials.totals.profit >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                    {monthlyFinancials.totals.profit >= 0 ? '+' : ''}{formatCurrency(monthlyFinancials.totals.profit)}
                  </td>
                  <td className="py-3 text-right font-oswald font-bold text-gray-600">{monthlyFinancials.totals.hours.toFixed(1)}h</td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      )}

      {/* Notes */}
      {client.notes && (
        <div className="bg-white border-2 border-black p-6 shadow-[4px_4px_0px_0px_rgba(0,0,0,1)]">
          <h2 className="font-oswald text-xl font-bold uppercase tracking-tight mb-4">
            Poznámky
          </h2>
          <p className="font-serif text-gray-700 whitespace-pre-wrap">{client.notes}</p>
        </div>
      )}
    </div>
  );
};

export default ClientDetail;
